/**
 * The W2 domain: the machinery board, the command ledger, run control beyond the four verbs
 * `knowitAutomation.ts` already had, flow health/billing, the delivery ledger, document tasks,
 * nudges and directives.
 *
 * Lives beside `aiAdminTypes.ts` for the same reason that file lives beside `types.ts`: every
 * endpoint here answers a bare object, or a bare object whose top-level key happens to be
 * `data` (never the `{data, policies}` envelope) — see W2-automation-operations.md §0.1. Schemas
 * are zod so the golden-fixture tests can assert the parsed shape rather than a hand-written
 * sample of it.
 */
import { z } from "zod";

// ── The machinery board (tasks.list) ─────────────────────────────────────────

export const TaskHealthLevelSchema = z.enum(["ok", "warn", "error"]);
export type TaskHealthLevel = z.infer<typeof TaskHealthLevelSchema>;

export const TaskHealthSchema = z.object({
  level: TaskHealthLevelSchema,
  note: z.string().nullable(),
});
export type TaskHealth = z.infer<typeof TaskHealthSchema>;

export const TaskScheduleSchema = z
  .object({
    nextFireAt: z.string().nullable().default(null),
    lastFiredAt: z.string().nullable().default(null),
    expiresAt: z.string().nullable().default(null),
    recurrence: z.string().nullable().default(null),
    triggerKind: z.string().nullable().default(null),
  })
  .nullable();
export type TaskSchedule = z.infer<typeof TaskScheduleSchema>;

export const TaskProgressSchema = z
  .object({
    nodesDone: z.number().default(0),
    nodesTotal: z.number().default(0),
    cursorNode: z.string().nullable().default(null),
  })
  .nullable();
export type TaskProgress = z.infer<typeof TaskProgressSchema>;

export const TaskSpendSchema = z
  .object({
    tokens: z.number().nullable().default(null),
    input: z.number().nullable().default(null),
    cacheWrite: z.number().nullable().default(null),
    cacheRead: z.number().nullable().default(null),
    output: z.number().nullable().default(null),
    budget: z.number().nullable().default(null),
    requests: z.number().nullable().default(null),
    actions: z.number().nullable().default(null),
    amplification: z.number().nullable().default(null),
    cacheReadShare: z.number().nullable().default(null),
  })
  .nullable();
export type TaskSpend = z.infer<typeof TaskSpendSchema>;

export const TaskTargetSchema = z
  .object({
    ideSessionKey: z.string().nullable().default(null),
    machine: z.string().nullable().default(null),
    workspace: z.string().nullable().default(null),
    agent: z.string().nullable().default(null),
    /** "partial" = Copilot has no transcript; its tokens are excluded, never reported as zero. */
    telemetry: z.enum(["full", "partial"]).nullable().default(null),
    executorPid: z.number().nullable().default(null),
    canStep: z.boolean().default(false),
    stepMode: z.boolean().default(false),
    ideLive: z.boolean().default(false),
  })
  .nullable();
export type TaskTarget = z.infer<typeof TaskTargetSchema>;

/**
 * `links` differs by source and the kms type is wrong about it (F-8): scheduled rows send
 * `{decisionId, flowId}`, run rows send `{run, decisionId, flowSlug, flowVersion}` — flowSlug,
 * not flowId — and dispatch rows send `{run}` only. Typed honestly as one optional bag rather
 * than a discriminated union, because the server does not tag which shape it sent.
 */
export const TaskLinksSchema = z
  .object({
    decisionId: z.string().nullable().optional(),
    flowId: z.string().nullable().optional(),
    flowSlug: z.string().nullable().optional(),
    flowVersion: z.number().nullable().optional(),
    run: z.string().nullable().optional(),
  })
  .default({});
export type TaskLinks = z.infer<typeof TaskLinksSchema>;

export const TASK_SOURCES = ["scheduled", "run", "dispatch"] as const;
export type TaskSource = (typeof TASK_SOURCES)[number];

export const TaskRowSchema = z.object({
  id: z.string(),
  source: z.enum(TASK_SOURCES),
  title: z.string(),
  kind: z.string(),
  state: z.string(),
  schedule: TaskScheduleSchema,
  progress: TaskProgressSchema,
  spend: TaskSpendSchema,
  target: TaskTargetSchema,
  health: TaskHealthSchema,
  links: TaskLinksSchema,
  lastActionAt: z.string().nullable().default(null),
});
export type TaskRow = z.infer<typeof TaskRowSchema>;

export const TaskBoardSchema = z.object({
  tasks: z.array(TaskRowSchema),
  worstHealth: TaskHealthLevelSchema,
  warnings: z.array(z.string()),
  infrastructure: z.object({
    workerProcessingJobs: z.boolean(),
    liveIdeSessions: z.number(),
  }),
  checkedAt: z.string(),
});
export type TaskBoard = z.infer<typeof TaskBoardSchema>;

// ── The command ledger (ai-actions.*) ────────────────────────────────────────

export const ActionTokensSchema = z.object({
  input: z.number().default(0),
  cacheWrite: z.number().default(0),
  cacheRead: z.number().default(0),
  output: z.number().default(0),
  total: z.number().default(0),
});
export type ActionTokens = z.infer<typeof ActionTokensSchema>;

/**
 * `costMicros` rides here per the wire, but it is a standing estate rule that it is never
 * trustworthy (F-14) — nothing in Slate may render a currency figure from it.
 */
export const ActionRowSchema = z.object({
  id: z.string(),
  runId: z.string().nullable(),
  stepId: z.string().nullable(),
  seq: z.number(),
  kind: z.string(),
  status: z.string(),
  summary: z.string().nullable(),
  summaryGeneric: z.boolean().default(false),
  toolName: z.string().nullable(),
  toolInput: z.string().nullable(),
  resultBytes: z.number().nullable(),
  resultDigest: z.string().nullable(),
  model: z.string().nullable(),
  effort: z.string().nullable(),
  thinkerId: z.string().nullable(),
  thinker: z.string().nullable(),
  tokens: ActionTokensSchema,
  costMicros: z.number().nullable(),
  actorUserId: z.string().nullable(),
  requestId: z.string().nullable(),
  sessionKey: z.string().nullable(),
  startedAt: z.string().nullable(),
  endedAt: z.string().nullable(),
  durationMs: z.number().nullable(),
  error: z.string().nullable(),
});
export type ActionRow = z.infer<typeof ActionRowSchema>;

export const LedgerPageSchema = z.object({
  total: z.number(),
  limit: z.number(),
  offset: z.number(),
  actions: z.array(ActionRowSchema),
});
export type LedgerPage = z.infer<typeof LedgerPageSchema>;

/**
 * Nine values, not the eight kms offers: `actor` answers "who spent it", which none of the
 * others can (F-7). Slate adds it to the group-by select.
 */
export const REPORT_GROUP_BYS = [
  "tool",
  "model",
  "effort",
  "node",
  "command_family",
  "thinker",
  "kind",
  "status",
  "actor",
] as const;
export type ReportGroupBy = (typeof REPORT_GROUP_BYS)[number];

export const ReportRowSchema = z.object({
  key: z.string(),
  calls: z.number(),
  inputTokens: z.number(),
  cacheWriteTokens: z.number(),
  cacheReadTokens: z.number(),
  outputTokens: z.number(),
  totalTokens: z.number(),
  resultBytes: z.number(),
  costMicros: z.number().nullable(),
});
export type ReportRow = z.infer<typeof ReportRowSchema>;

export const ReportTotalsSchema = z.object({
  input: z.number(),
  cacheWrite: z.number(),
  cacheRead: z.number(),
  output: z.number(),
  total: z.number(),
  contentTokens: z.number(),
  amplification: z.number().nullable(),
  cacheReadShare: z.number().nullable(),
  actions: z.number(),
  requests: z.number(),
  costMicros: z.number().nullable(),
});
export type ReportTotals = z.infer<typeof ReportTotalsSchema>;

export const LedgerReportSchema = z.object({
  groupBy: z.string(),
  rows: z.array(ReportRowSchema),
  totals: ReportTotalsSchema,
});
export type LedgerReport = z.infer<typeof LedgerReportSchema>;

/** `contentTokens = Σ resultBytes ÷ 4` is a heuristic (F-12); amplification is built on it. */
export const EfficiencyNodeSchema = z.object({
  nodeId: z.string(),
  index: z.number(),
  requests: z.number(),
  actions: z.number(),
  filesWritten: z.number(),
  contentTokens: z.number(),
  billedTokens: z.number(),
  cacheReadTokens: z.number(),
  cacheReadShare: z.number().nullable(),
  amplification: z.number().nullable(),
  models: z.array(z.string().nullable()),
  effortMax: z.string().nullable(),
});
export type EfficiencyNode = z.infer<typeof EfficiencyNodeSchema>;

export const RunEfficiencySchema = z.object({
  runId: z.string(),
  nodes: z.array(EfficiencyNodeSchema),
  run: z.object({
    billedTokens: z.number(),
    contentTokens: z.number(),
    amplification: z.number().nullable(),
    requests: z.number(),
    actions: z.number(),
    tokenBudget: z.number().nullable(),
    budgetAction: z.string(),
    budgetTrippedAt: z.string().nullable(),
  }),
  worstNode: z.string().nullable(),
  /**
   * NOT sent by the server (F-11) — derived client-side as
   * `Σ nodes[].cacheReadTokens ÷ run.billedTokens`. Labelled as derived wherever it is shown.
   */
  runCacheReadShare: z.number().nullable(),
});
export type RunEfficiency = z.infer<typeof RunEfficiencySchema>;

export const BudgetAckSchema = z.object({
  ok: z.boolean(),
  runId: z.string(),
  tokenBudget: z.number().nullable(),
  budgetAction: z.string(),
  spent: z.number(),
});
export type BudgetAck = z.infer<typeof BudgetAckSchema>;

// ── Runs: control verbs and detail beyond the four `knowitAutomation.ts` already had ────────

export const DegradedSourceSchema = z.object({
  nodeId: z.string(),
  sourceId: z.string(),
  kind: z.string(),
  reason: z.string(),
});
export type DegradedSource = z.infer<typeof DegradedSourceSchema>;

export const ChildRunSummarySchema = z.object({
  runId: z.string(),
  name: z.string(),
  status: z.string(),
  aiFlowSlug: z.string().nullable(),
  startedAt: z.string().nullable(),
  endedAt: z.string().nullable(),
  currentNodeId: z.string().nullable(),
  nodesDone: z.number(),
  nodesTotal: z.number(),
  via: z.enum(["amend", "subflow"]),
});
export type ChildRunSummary = z.infer<typeof ChildRunSummarySchema>;

export const StopAckSchema = z.object({
  ok: z.boolean(),
  mode: z.enum(["graceful", "immediate"]),
  honoredAt: z.string(),
  executor: z
    .object({ ideSessionKey: z.string().nullable(), pid: z.number().nullable() })
    .nullable(),
  executorNote: z.string(),
});
export type StopAck = z.infer<typeof StopAckSchema>;

export const StepAckSchema = z.object({
  ok: z.boolean(),
  stepMode: z.boolean(),
  released: z.boolean().nullable().default(null),
  honoredAt: z.string(),
  executor: z.unknown().nullable(),
  host: z.string().nullable().default(null),
});
export type StepAck = z.infer<typeof StepAckSchema>;

export const JUMP_MODES = ["rewind", "skip-to", "retry-node"] as const;
export type JumpMode = (typeof JUMP_MODES)[number];

export interface JumpRequest {
  toNodeId: string;
  mode?: JumpMode;
  note?: string;
}

export const JumpAckSchema = z.object({
  ok: z.boolean(),
  mode: z.string(),
  toNodeId: z.string(),
  invalidated: z.array(z.object({ nodeId: z.string(), index: z.number(), was: z.string() })),
  warning: z.string().nullable(),
  executor: z.unknown().nullable(),
  executorNote: z.string(),
});
export type JumpAck = z.infer<typeof JumpAckSchema>;

export interface PublishDestination {
  kind: "document";
  collectionId: string;
  parentDocumentId?: string;
  titleTemplate?: string;
  mode?: "create" | "append" | "replace";
}

export interface PublishOptions {
  artifactPath?: string;
  destination?: PublishDestination;
}

export const PublishResultSchema = z.object({
  ok: z.boolean(),
  documentId: z.string(),
  title: z.string(),
  url: z.string(),
  mode: z.string(),
});
export type PublishResult = z.infer<typeof PublishResultSchema>;

export const RunArtifactSchema = z.object({
  path: z.string(),
  version: z.number(),
  nodeId: z.string().nullable(),
  mediaType: z.string().nullable(),
  bytes: z.number(),
  sha256: z.string().nullable(),
  producedBy: z.string().nullable(),
  createdAt: z.string(),
  hasContent: z.boolean(),
  /**
   * Not on the wire object itself — annotated client-side from the fixture/adapter so the
   * Content column can show a `sensitive` badge without a second round trip. Real deployments
   * learn this only when a fetch is refused; see `knowitOps.ts`.
   */
  sensitive: z.boolean().default(false),
});
export type RunArtifact = z.infer<typeof RunArtifactSchema>;

export const ArtifactContentSchema = z.object({
  path: z.string(),
  version: z.number(),
  nodeId: z.string().nullable(),
  mediaType: z.string().nullable(),
  bytes: z.number(),
  sha256: z.string().nullable(),
  producedBy: z.string().nullable(),
  createdAt: z.string(),
  content: z.string().nullable(),
  contentWithheld: z.boolean(),
});
export type ArtifactContent = z.infer<typeof ArtifactContentSchema>;

/** `runs.get`'s response, kept apart from the thin `Run` the list uses (D.2 rule 4). */
/**
 * Weave (Docit Work 15 §7.2) — `runs.tree`: what each node of a run IS. One read behind the run
 * canvas's node cards and the tile tree: the executor kind, the tile the node carries (key, kind,
 * state, category, assignee, blocked/held), the edges, and the run's root tile.
 *
 * A run of an ordinary flow answers with `isProject: false`, `root: null` and every `tile: null` —
 * the canvas then draws exactly what it drew before Weave existed.
 */
export interface RunTreeTile {
  id: string;
  key: string;
  kind: string;
  state: string;
  category: string;
  title: string;
  priority: string | null;
  severity: string | null;
  estimate: number | null;
  blocked: boolean;
  blockedReason: string | null;
  held: boolean;
  dueAt: string | null;
  assignee: { kind: string; id: string | null } | null;
  role: string;
  parentNodeId: string | null;
  isRef: boolean;
  version: number;
}

export interface RunTreeNode {
  nodeId: string;
  label: string;
  /** The authored node kind (task | compute | work | human | fork | join | forEach | subflow). */
  kind: string;
  /** Who runs it — "mechanics" when the node carries no tile. */
  executor: string;
  ownerRole: string | null;
  model: string | null;
  requireApproval: boolean;
  tile: RunTreeTile | null;
  runStatus: string;
  queuedReason: string | null;
  branchId: string | null;
  attempts: number;
  questionKey: string | null;
}

export interface RunTree {
  runId: string;
  flowSlug: string | null;
  status: string;
  statusReason: string | null;
  isProject: boolean;
  reshapeSeq: number;
  root: {
    id: string;
    key: string;
    kind: string;
    title: string;
    state: string;
    category: string;
  } | null;
  nodes: RunTreeNode[];
  edges: Array<{ from: string; to: string; condition: string }>;
}

export interface RunDetail {
  run: import("~/api/types").Run;
  host: "server" | "ide" | null;
  statusReason: string | null;
  /** Passed through as raw JSON; the run-viewer builder (W2b) shapes it against FlowDefinition. */
  graph: Record<string, unknown> | null;
  causalRunId: string | null;
  children: ChildRunSummary[];
}

/** Built lazily against `RunSchema` (in `types.ts`) by importers that need to validate a
 * `RunDetail` — `RunSchema` cannot be imported here without a cycle, so `http.ts` composes this
 * shape itself from `RunSchema` + the fields above. Exported for that one caller. */
export const RunDetailShape = {
  host: z.enum(["server", "ide"]).nullable(),
  statusReason: z.string().nullable(),
  graph: z.record(z.string(), z.unknown()).nullable(),
  causalRunId: z.string().nullable(),
  children: z.array(ChildRunSummarySchema),
};

// ── Directives (steering) ─────────────────────────────────────────────────────

export const DirectiveSchema = z.object({
  id: z.string(),
  flowId: z.string(),
  text: z.string(),
  createdBy: z.string().nullable(),
  active: z.boolean(),
  createdAt: z.string(),
  revokedAt: z.string().nullable(),
  revokedBy: z.string().nullable(),
});
export type Directive = z.infer<typeof DirectiveSchema>;

// ── IDE sessions (Task Center's target-window picker) ────────────────────────

export const IdeSessionSchema = z.object({
  id: z.string(),
  sessionKey: z.string(),
  userId: z.string().nullable(),
  userName: z.string().nullable(),
  isOwn: z.boolean(),
  machineName: z.string().nullable(),
  workspaceName: z.string().nullable(),
  workspacePath: z.string().nullable(),
  extensionVersion: z.string().nullable(),
  bridgeVersion: z.string().nullable(),
  machineId: z.string().nullable(),
  boundTeamId: z.string().nullable(),
  /** The workspace the window is registered in (where its token acts); absent on older servers. */
  teamId: z.string().nullable().optional(),
  teamName: z.string().nullable().optional(),
  bindingMismatch: z.boolean().nullable(),
  capabilities: z.array(z.string()),
  startedAt: z.string(),
  lastSeenAt: z.string(),
  revokedAt: z.string().nullable(),
  isOnline: z.boolean(),
});
export type IdeSession = z.infer<typeof IdeSessionSchema>;

// ── Task Center create drafts + the prompt-composer proposal ────────────────

export type TriggerDraft =
  | { kind: "every"; everyMinutes: number }
  | { kind: "at"; atUtc: string }
  /** BRIEF-CGNI S2-6: fires when this workspace publishes the event (a notification-taxonomy id). */
  | { kind: "on"; onEvent: string };

export interface DigestTaskDraft {
  title?: string;
  topic?: string;
  sourceMode: "search" | "url" | "rss";
  sources?: string[];
  thinkerId?: string;
  targetDocumentId: string;
  outputMode: "prepend" | "replace";
  notifyInApp?: boolean;
  trigger: TriggerDraft;
}

export interface FlowTaskDraft {
  title: string;
  flowId: string;
  trigger: TriggerDraft;
}

/** `search` is the /search box in prompt mode (search-as-a-prompt S1): same action set as `app`, navigate first. */
export type RouteSurface = "tasks" | "flows" | "app" | "document" | "collection" | "search";

/**
 * `ai-actions/route` answers **one endpoint with seven shapes**, discriminated by `actionId`
 * (W7 §B1.1). W2 parsed only the digest member, which is why the other six read as an empty
 * digest proposal; the union below is the whole contract, and `normalizeRouteProposal` is the
 * single parser both transports share — the RPC adapter calls it directly on the bare object
 * and the REST schema calls it through a transform, so the two can never drift.
 */

/**
 * What a navigate target IS. The server resolves all four (S1, S4); an older server sends none,
 * which reads as `document`. `work` is a Docit Work tile (S4): its `path` is `/work/{KEY}` and
 * its title is "KEY · <item title>".
 */
export type NavigateTargetKind = "document" | "research" | "collection" | "work";

export const NAVIGATE_TARGET_KINDS: readonly NavigateTargetKind[] = [
  "document",
  "research",
  "collection",
  "work",
];

/**
 * A navigate target, and the shape `alternates` carries on every response.
 *
 * `path` is the slate route the server resolved (`/documents/{id}`, `/research?research={id}`,
 * `/collections/{id}`, `/work/{KEY}`) and is what the client navigates to — never a route
 * rebuilt from the id, because only the server knows which kind of thing the id names.
 */
export interface NavigateTarget {
  id: string;
  title: string;
  path: string;
  kind: NavigateTargetKind;
}

export function navigateTargetKind(value: unknown): NavigateTargetKind {
  return NAVIGATE_TARGET_KINDS.includes(value as NavigateTargetKind)
    ? (value as NavigateTargetKind)
    : "document";
}

export interface DigestProposal {
  topic: string | null;
  sourceMode: string | null;
  sources: string[];
  thinkerId: string | null;
  targetDocumentId: string | null;
  targetDocumentTitle: string | null;
  outputMode: string | null;
  notifyInApp: boolean | null;
  maxSources: number | null;
}

export interface NudgeProposal {
  targetType: string | null;
  documentId: string | null;
  /** Null when the server could not resolve a time, or resolved one in the past. */
  whenUtc: string | null;
  timezone: string | null;
  recurrenceRule: string | null;
  note: string | null;
}

export interface DocumentProposal {
  title: string | null;
  text: string | null;
  /** ⚠ Team-scoped but NOT ACL-scoped server-side (W7 §E2 R4) — re-validate before offering it. */
  collectionId: string | null;
  collectionTitle: string | null;
}

export interface SectionProposal {
  documentId: string | null;
  heading: string | null;
  text: string | null;
  requestedLane: string | null;
}

export interface NotifyProposal {
  kind: string | null;
  message: string | null;
  audienceRole: "admin" | "member" | "viewer" | null;
  documentId: string | null;
  collectionId: string | null;
  audienceSummary: string | null;
}

/** Everything every shape carries, whatever the intent turned out to be. */
export interface RouteCommon {
  intent: string | null;
  /**
   * `NormalizeConfidence` server-side: clamped to [0,1], and **missing or NaN reads as 0.7**,
   * deliberately — so a shape the model did not score still lands on the confirm card.
   */
  confidence: number;
  alternates: NavigateTarget[];
  surface: string | null;
  summary: string | null;
  title: string | null;
  /** Every gap the server could not fill. A non-empty list disables the confirm's primary. */
  missing: string[];
  message: string | null;
}

export type RouteProposal =
  | (RouteCommon & {
      actionId: null;
      /**
       * ⚠ The server sends the surface **NAME**, not a flag. It was typed `boolean` and coerced
       * with `value === true`, so every refusal read `false` (W7 §E6.7). Fixed here.
       */
      unsupportedSurface: string | null;
    })
  | (RouteCommon & {
      actionId: "create-digest";
      digest: DigestProposal | null;
      trigger: TriggerDraft | null;
    })
  | (RouteCommon & { actionId: "create-nudge"; nudge: NudgeProposal })
  | (RouteCommon & { actionId: "create-document"; document: DocumentProposal })
  | (RouteCommon & { actionId: "navigate"; document: NavigateTarget | null })
  | (RouteCommon & { actionId: "edit-section"; section: SectionProposal | null })
  | (RouteCommon & { actionId: "notify"; notify: NotifyProposal })
  | (RouteCommon & { actionId: "taskize" });

/** The server's own default. A shape with no score still reaches the confirm card. */
export const ROUTE_CONFIDENCE_DEFAULT = 0.7;

/** Auto-navigate at or above this; below it the card is shown with its alternates. */
export const NAVIGATE_AUTO_CONFIDENCE = 0.8;

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function text(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

function flag(value: unknown): boolean | null {
  return typeof value === "boolean" ? value : null;
}

function count(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function strings(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((entry): entry is string => typeof entry === "string")
    : [];
}

function targets(value: unknown): NavigateTarget[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value
    .map((entry) => asRecord(entry))
    .filter((entry) => typeof entry.id === "string" && typeof entry.path === "string")
    .map((entry) => ({
      id: String(entry.id),
      title: text(entry.title) ?? "",
      path: String(entry.path),
      kind: navigateTargetKind(entry.kind),
    }));
}

/** `NormalizeConfidence`, client-side: clamp to [0,1]; missing or NaN reads as 0.7. */
export function normalizeConfidence(value: unknown): number {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return ROUTE_CONFIDENCE_DEFAULT;
  }
  return Math.min(1, Math.max(0, value));
}

function trigger(value: unknown): TriggerDraft | null {
  const raw = asRecord(value);
  if (raw.kind === "at" && typeof raw.atUtc === "string") {
    return { kind: "at", atUtc: raw.atUtc };
  }
  if (raw.kind === "every") {
    return { kind: "every", everyMinutes: count(raw.everyMinutes) ?? 1440 };
  }
  if (raw.kind === "on" && typeof raw.onEvent === "string" && raw.onEvent.trim()) {
    return { kind: "on", onEvent: raw.onEvent.trim() };
  }
  return null;
}

/**
 * The one parser. Every unknown `actionId` — including the ones a future server adds — falls
 * to the refusal member rather than to a half-filled digest, which is what W2's single-shape
 * parser did.
 */
export function normalizeRouteProposal(value: unknown): RouteProposal {
  const raw = asRecord(value);
  const common: RouteCommon = {
    intent: text(raw.intent),
    confidence: normalizeConfidence(raw.confidence),
    alternates: targets(raw.alternates),
    surface: text(raw.surface),
    summary: text(raw.summary),
    title: text(raw.title),
    missing: strings(raw.missing),
    message: text(raw.message),
  };
  const actionId = text(raw.actionId);

  switch (actionId) {
    case "create-digest": {
      const digest = raw.digest ? asRecord(raw.digest) : null;
      return {
        ...common,
        actionId: "create-digest",
        digest: digest
          ? {
              topic: text(digest.topic),
              sourceMode: text(digest.sourceMode),
              sources: strings(digest.sources),
              thinkerId: text(digest.thinkerId),
              targetDocumentId: text(digest.targetDocumentId),
              targetDocumentTitle: text(digest.targetDocumentTitle),
              outputMode: text(digest.outputMode),
              notifyInApp: flag(digest.notifyInApp),
              maxSources: count(digest.maxSources),
            }
          : null,
        trigger: trigger(raw.trigger),
      };
    }
    case "create-nudge": {
      const nudge = asRecord(raw.nudge);
      return {
        ...common,
        actionId: "create-nudge",
        nudge: {
          targetType: text(nudge.targetType) ?? "document",
          documentId: text(nudge.documentId),
          whenUtc: text(nudge.whenUtc),
          timezone: text(nudge.timezone),
          recurrenceRule: text(nudge.recurrenceRule),
          note: text(nudge.note),
        },
      };
    }
    case "create-document": {
      const document = asRecord(raw.document);
      return {
        ...common,
        actionId: "create-document",
        document: {
          title: text(document.title),
          text: text(document.text),
          collectionId: text(document.collectionId),
          collectionTitle: text(document.collectionTitle),
        },
      };
    }
    case "navigate": {
      const document = raw.document ? asRecord(raw.document) : null;
      return {
        ...common,
        actionId: "navigate",
        document:
          document && typeof document.id === "string" && typeof document.path === "string"
            ? {
                id: document.id,
                title: text(document.title) ?? "",
                path: document.path,
                kind: navigateTargetKind(document.kind),
              }
            : null,
      };
    }
    case "edit-section": {
      const section = raw.section ? asRecord(raw.section) : null;
      return {
        ...common,
        actionId: "edit-section",
        section: section
          ? {
              documentId: text(section.documentId),
              heading: text(section.heading),
              text: text(section.text),
              requestedLane: text(section.requestedLane),
            }
          : null,
      };
    }
    case "notify": {
      const notify = asRecord(raw.notify);
      const role = text(notify.audienceRole);
      return {
        ...common,
        actionId: "notify",
        notify: {
          kind: text(notify.kind),
          message: text(notify.message),
          audienceRole: role === "admin" || role === "member" || role === "viewer" ? role : null,
          documentId: text(notify.documentId),
          collectionId: text(notify.collectionId),
          audienceSummary: text(notify.audienceSummary),
        },
      };
    }
    case "taskize":
      return { ...common, actionId: "taskize" };
    default:
      return { ...common, actionId: null, unsupportedSurface: text(raw.unsupportedSurface) };
  }
}

/**
 * REST transport shape. It is a transform over the raw body rather than a structural schema
 * because the seven members share no field set — and because both transports must land on the
 * SAME parser, or the fixture tests pin only one of them.
 */
export const RouteProposalSchema = z.unknown().transform(normalizeRouteProposal);

// ── Flow health / billing ─────────────────────────────────────────────────────

export const BillingRecentRunSchema = z.object({
  runId: z.string(),
  name: z.string().nullable(),
  /** Null means the run record was purged; billing rows outlive run retention by design. */
  status: z.string().nullable(),
  tokensIn: z.number(),
  tokensOut: z.number(),
  source: z.string().nullable(),
  occurredAt: z.string(),
});
export type BillingRecentRun = z.infer<typeof BillingRecentRunSchema>;

export const BillingUsageSchema = z.object({
  windowDays: z.number(),
  ai: z.object({ tokensIn: z.number(), tokensOut: z.number(), total: z.number() }),
  storage: z.object({ artifactBytes: z.number(), attachmentBytes: z.number() }),
  daily: z.array(z.object({ date: z.string(), tokensIn: z.number(), tokensOut: z.number() })),
  recentRuns: z.array(BillingRecentRunSchema),
});
export type BillingUsage = z.infer<typeof BillingUsageSchema>;

/** `TeamPreference` keys, reproduced verbatim from the Outline shared tree (E.2). */
export const FlowHealthSettingsSchema = z.object({
  flowsNotifyOnFailure: z.boolean(),
  flowsBillingEnabled: z.boolean(),
  /** Null = server default (90). */
  flowsAuditRetentionDays: z.number().nullable(),
});
export type FlowHealthSettings = z.infer<typeof FlowHealthSettingsSchema>;
export type FlowHealthSettingsPatch = Partial<FlowHealthSettings>;

// ── Delivery ledger ────────────────────────────────────────────────────────────

export const DELIVERY_CHANNELS = ["app", "email", "chat", "webhook"] as const;
export type DeliveryChannel = (typeof DELIVERY_CHANNELS)[number];
export const DELIVERY_STATUSES = ["sent", "failed", "skipped"] as const;
export type DeliveryStatus = (typeof DELIVERY_STATUSES)[number];

export const DeliveryRowSchema = z.object({
  id: z.string(),
  notificationId: z.string().nullable(),
  event: z.string(),
  userId: z.string().nullable(),
  userName: z.string().nullable(),
  userEmail: z.string().nullable(),
  channel: z.string(),
  status: z.string(),
  attemptedAt: z.string(),
  error: z.string().nullable(),
  detail: z.string().nullable(),
});
export type DeliveryRow = z.infer<typeof DeliveryRowSchema>;

export const DeliveryPageSchema = z.object({
  offset: z.number(),
  limit: z.number(),
  total: z.number(),
  data: z.array(DeliveryRowSchema),
});
export type DeliveryPage = z.infer<typeof DeliveryPageSchema>;

// ── Document tasks ─────────────────────────────────────────────────────────────

export const DocumentTaskSchema = z.object({
  id: z.string(),
  documentId: z.string(),
  title: z.string(),
  details: z.string().nullable(),
  status: z.enum(["open", "done"]),
  assigneeId: z.string().nullable(),
  dueDate: z.string().nullable(),
  sourceQuote: z.string().nullable(),
  relatedDocumentIds: z.array(z.string()),
  createdById: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  completedAt: z.string().nullable(),
});
export type DocumentTask = z.infer<typeof DocumentTaskSchema>;

export interface DocumentTaskInput {
  documentId: string;
  title: string;
  details?: string;
  assigneeId?: string;
  dueDate?: string;
  sourceQuote?: string;
  relatedDocumentIds?: string[];
}

/**
 * `hasAssignee` / `hasDueDate` are the tri-state discriminators — without them the server
 * cannot tell "unchanged" from "cleared". `assigneeId`/`dueDate` of `null` clears; omitted
 * with the flag false/absent leaves the field alone.
 */
export interface DocumentTaskPatch {
  title?: string;
  details?: string;
  hasAssignee?: boolean;
  assigneeId?: string | null;
  hasDueDate?: boolean;
  dueDate?: string | null;
  relatedDocumentIds?: string[];
}

// ── Nudges ─────────────────────────────────────────────────────────────────────

export const NUDGE_TARGET_TYPES = ["document", "part", "task", "decision"] as const;
export type NudgeTargetType = (typeof NUDGE_TARGET_TYPES)[number];

export const NudgeSchema = z.object({
  id: z.string(),
  targetType: z.enum(NUDGE_TARGET_TYPES),
  documentId: z.string().nullable(),
  partId: z.string().nullable(),
  recordId: z.string().nullable(),
  whenUtc: z.string(),
  timezone: z.string(),
  recurrenceRule: z.string().nullable(),
  note: z.string().nullable(),
  channels: z.object({ inApp: z.boolean(), email: z.boolean() }),
  status: z.enum(["scheduled", "fired", "snoozed", "done", "cancelled"]),
  firedAtUtc: z.string().nullable(),
  createdAt: z.string(),
});
export type Nudge = z.infer<typeof NudgeSchema>;

export interface NudgeTarget {
  targetType: NudgeTargetType;
  documentId?: string;
  partId?: string;
  recordId?: string;
}

/** What QuickWhenPicker commits — §A.2. */
export interface QuickWhenResult {
  whenUtc: string;
  timezone: string;
  recurrenceRule?: string;
  note?: string;
}
